import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { ROUTE_PATH } from "utils/constants";
import { MAX_ACTIVE_MISSIONS } from "utils/constants/mission";
import {
  missionTypeFilterOptions,
  competitionFormatFilterOptions,
  perPageOptions,
  missionStatusFilterOptionsTabActive,
  missionStatusFilterOptionsHistory,
} from "utils/constants/options";
import { parseQueryString } from "utils/functions/parseQueryString";
import { renderMissionType, renderCompetitionFormat } from "utils/functions/companyMissionRender";
import { adminService } from "apiServices/apiController/adminService";
import { Buttons, FormFields, Icons, PaginationNormal, StatusTag, Tabs, TableCustom, Displays, Loading } from "components";
import {
  CompanyMissionV2ContainerStyled,
} from "./styled";
import renderTooltipCell from "utils/functions/renderTooltipCell";
import HeaderWithActions from "components/Displays/HeaderWithActions";
import moment from "moment";


const CompanyMissionV2Container = () => {
  const history = useHistory();
  const isInitialMount = useRef(true);
  const theme = useTheme();

  const filterDefaults = useMemo(() => ({
    keyword: "",
    mission_type: { label: "ทั้งหมด", value: "all" },
    competition_format: { label: "ทั้งหมด", value: "all" },
    status: { label: "ทั้งหมด", value: "all" },
  }), []);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: filterDefaults,
  });
  const [selectActiveTab, setSelectActiveTab] = useState("list");
  const [appliedFilters, setAppliedFilters] = useState({
    keyword: "",
    mission_type: "",
    competition_format: "",
    status: "",
  });
  const [isSearched, setIsSearched] = useState(false);
  const [perPage, setPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [activeMissionCount, setActiveMissionCount] = useState(0);
  const [isActiveMissionCountLoaded, setIsActiveMissionCountLoaded] = useState(false);
  const isCreateDisabled = !isActiveMissionCountLoaded || activeMissionCount >= MAX_ACTIVE_MISSIONS;

  const handleBack = useCallback(() => {
    history.push(ROUTE_PATH.BACK_OFFICE);
  }, [history]);

  const handleCreate = useCallback(() => {
    history.push(`${ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2}/create`);
  }, [history]);

  const handleViewDetail = useCallback(
    (id) => {
      history.push(`${ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2}/${id}`);
    },
    [history]
  );

  const handleSearch = useCallback(
    (formValues) => {
      const extractValue = (v) => {
        const raw = v?.value || "";
        return raw === "all" ? "" : raw;
      };
      setAppliedFilters({
        keyword: (formValues.keyword || "").trim(),
        mission_type: extractValue(formValues.mission_type),
        competition_format: extractValue(formValues.competition_format),
        status: extractValue(formValues.status),
      });
      setIsSearched(true);
      setCurrentPage(1);
    },
    []
  );

  const handleAuditLog = useCallback(() => {
    history.push(ROUTE_PATH.BACK_OFFICE_COMPANY_MISSION_V2_AUDIT_LOG);
  }, [history]);

  const fetchActiveMissionCount = useCallback(async () => {
    const res = await adminService.GET_MISSION_V2_ACTIVE_COUNT();
    if (res?.success) {
      setActiveMissionCount(res?.data?.count ?? res?.data?.length ?? res?.count ?? 0);
      setIsActiveMissionCountLoaded(true);
    }
  }, []);

  const fetchMissionList = useCallback(async () => {
    setIsLoading(true);
    const queryPayload = {
      per_page: perPage,
      offset: (currentPage - 1) * perPage,
    };
    if (appliedFilters.mission_type) {
      queryPayload.category = appliedFilters.mission_type;
    }
    if (appliedFilters.competition_format) {
      queryPayload.challenge_type = appliedFilters.competition_format;
    }
    if (appliedFilters.status) {
      queryPayload.status = appliedFilters.status;
    }
    if (appliedFilters.keyword) {
      queryPayload.keyword = appliedFilters.keyword;
    }
    queryPayload.expiry = selectActiveTab === "list" ? "not_expired" : "expired";
    const stringQuery = parseQueryString(queryPayload);
    const res = await adminService.GET_MISSION_V2_LIST(stringQuery);
    if (res?.success) {
      setData(res?.data || []);
      setPagination(res?.page ? { ...res.page } : null);
    }
    setIsLoading(false);
  }, [appliedFilters, currentPage, perPage, selectActiveTab]);




  const handleChangePerPage = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleClickPage = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const tabHeaders = useMemo(() => [
    { label: "รายการภารกิจ", value: "list" },
    { label: "ประวัติภารกิจ", value: "history" },
  ], []);


  const columns = useMemo(() => {
    const cellEllipsis = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };

    const iconCellStyle = {
      display: "flex",
      alignItems: "center",
      gap: "6px",
    };

    return [
      {
        title: "รหัสภารกิจ",
        field: "code",
        headerStyle: { textAlign: "left", maxWidth: "5%" },
        style: { textAlign: "left", maxWidth: "5%", ...cellEllipsis },
        render: ({ value }) => <span style={cellEllipsis}>{value ?? "-"}</span>,
      },
      {
        title: "ชื่อภารกิจ",
        field: "name_th",
        headerStyle: { textAlign: "left", maxWidth: "30%" },
        style: {
          textAlign: "left",
          maxWidth: "30%",
          ...cellEllipsis,
        },
        render: ({ value, origin }) =>
          renderTooltipCell({
            cursor: "pointer",
            valueTooltip: <span>{value ?? '-'}</span>,
            tooltipId: `tooltipName${origin?.id ?? value}`,
            value: <span style={{ color: theme.COLORS.PURPLE_1, fontFamily: theme.FONT.STYLE.MEDIUM }}>{value ?? "-"}</span>,
          }),
      },
      {
        title: "ประเภทภารกิจ",
        field: "category",
        headerStyle: { textAlign: "left", maxWidth: "15%" },
        style: { textAlign: "left", maxWidth: "15%", ...cellEllipsis },
        render: ({ value }) => {
          const { label, icon } = renderMissionType(value);
          return (
            <span style={iconCellStyle}>
              {icon}
              <span style={cellEllipsis}>{label}</span>
            </span>
          );
        },
      },
      {
        title: "รูปแบบการแข่งขัน",
        field: "challenge_type",
        headerStyle: { textAlign: "left", maxWidth: "15%" },
        style: {
          textAlign: "left",
          maxWidth: "15%",
          ...cellEllipsis,
        },
        render: ({ value }) => {
          const { label, icon } = renderCompetitionFormat(value);
          return (
            <span style={iconCellStyle}>
              {icon}
              <span style={cellEllipsis}>{label ?? '-'}</span>
            </span>
          );
        },
      },
      {
        title: "ระยะเวลาภารกิจ",
        field: "period",
        headerStyle: { textAlign: "left", maxWidth: "15%" },
        style: { textAlign: "left", maxWidth: "15%", ...cellEllipsis },
        render: ({ origin }) => (
          <span>
            {moment(origin?.begin_date).add(543, "years").format("DD/MM/YYYY")} - {moment(origin?.end_date).add(543, "years").format("DD/MM/YYYY")}
          </span>
        ),
      },
      {
        title: "สถานะ",
        field: "status",
        headerStyle: { textAlign: "left", maxWidth: "10%" },
        style: { textAlign: "left", maxWidth: "10%" },
        render: ({ value }) => (
          <StatusTag variant="company_mission" status={value} />
        ),
      },
      {
        title: "",
        field: "actions",
        headerStyle: {
          textAlign: "center",
          maxWidth: "5%",
          flexShrink: 0,
        },
        style: {
          textAlign: "center",
          maxWidth: "5%",
          flexShrink: 0,
        },
        render: () =>
          selectActiveTab === "list" ? (
            <Icons.Pencil3
              color={theme.COLORS.PURPLE_1}
              width="28"
              height="28"
            />
          ) : (
            <Icons.Document />
          ),
      },
    ];
  }, [theme.COLORS.PURPLE_1, theme.FONT.STYLE.MEDIUM, selectActiveTab]);

  const handleChangeTab = useCallback((tab) => {
    setSelectActiveTab(tab.value);
    reset(filterDefaults);
    setAppliedFilters({
      keyword: "",
      mission_type: "",
      competition_format: "",
      status: "",
    });
    setIsSearched(false);
    setCurrentPage(1);
  }, [reset, filterDefaults]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      Promise.all([fetchMissionList(), fetchActiveMissionCount()]);
    } else {
      fetchMissionList();
    }
  }, [fetchMissionList, fetchActiveMissionCount]);

  return (
    <CompanyMissionV2ContainerStyled>
      <div className="content_section">
        <HeaderWithActions
          theme_admin
          handleBackCustom={handleBack}
          title="ภารกิจองค์กร"
          subTitle="Company mission"
          typeObj={
            <div style={{ width: "140px" }}>
              <Buttons.BtnNormal
                theme_only_border_blue
                fontSize="16px"
                label="สร้างภารกิจ"
                paddingButton="11px"
                disabled={isCreateDisabled}
                svg_front={<Icons.AddPlus color={isCreateDisabled ? theme.COLORS.GRAY_26 : theme.COLORS.PURPLE_1} />}
                onClick={handleCreate}
              />
            </div>}
        />

        <div className="tab_count_row">
          <Tabs.TabBarSelect
            tab_line
            tabHeader={tabHeaders}
            onClickSeletedTab={(tab) => {
              handleChangeTab(tab);
            }}
          />
          {selectActiveTab === 'list' &&
            <div className="mission_count_label">
              <span>จำนวน</span>
              <span>:</span>
              <span className="mission_count_value">
                {`${String(activeMissionCount).padStart(2, "0")}/${String(MAX_ACTIVE_MISSIONS).padStart(2, "0")}`}
              </span>
            </div>
          }
        </div>

        <form
          className="filter_section"
          onSubmit={handleSubmit(handleSearch)}
        >
          <div className="search_item">
            <FormFields.FormTextField
              control={control}
              labelFontFamily={theme.FONT.STYLE.MEDIUM}
              labelSize="14px"
              name="keyword"
              label="ค้นหา"
              placeholder="ค้นหาชื่อภารกิจ, รหัสภารกิจ"
              theme_admin
              fontSize="16px"
              iconRight={<Icons.Search3 color={theme.COLORS.GRAY_4} />}
            />
          </div>
          <div className="dropdown_item">
            <FormFields.FormAutoSuggestDropdown
              control={control}
              labelSize="14px"
              labelFontFamily={theme.FONT.STYLE.MEDIUM}
              name="mission_type"
              label="ประเภทภารกิจ"
              options={missionTypeFilterOptions}
              placeholder="ทั้งหมด"
              showAllOption
              showSearch={false}
            />
          </div>
          <div className="dropdown_item">
            <FormFields.FormAutoSuggestDropdown
              labelSize="14px"
              labelFontFamily={theme.FONT.STYLE.MEDIUM}
              control={control}
              name="competition_format"
              label="รูปแบบการแข่งขัน"
              options={competitionFormatFilterOptions}
              placeholder="ทั้งหมด"
              showAllOption
              showSearch={false}
            />
          </div>
          <div className="dropdown_item">
            <FormFields.FormAutoSuggestDropdown
              labelSize="14px"
              labelFontFamily={theme.FONT.STYLE.MEDIUM}
              control={control}
              name="status"
              label="สถานะ"
              options={selectActiveTab === "list" ? missionStatusFilterOptionsTabActive : missionStatusFilterOptionsHistory}
              placeholder="ทั้งหมด"
              showAllOption
              showSearch={false}
            />
          </div>
          <div>
            <Buttons.BtnNormal
              theme_only_border_blue
              paddingButton="12px 24.5px"
              fontSize="16px"
              label="ค้นหา"
              type="submit"
            />
          </div>
          <div className="filter_buttons">
            <Buttons.BtnNormal
              theme_bg_white
              paddingButton="12px 55.5px"
              fontSize="16px"
              label="Log"
              onClick={handleAuditLog}
            />
          </div>
        </form>

        <div className="table_section">
          {isLoading ? (
            <div className="mission_list_loading">
              <Loading theme_standard_loading />
            </div>
          ) : (
            <TableCustom.TableNormal
              theme_table_new
              columns={columns}
              data={data}
              showPagination={false}
              labelEmpty={
                <Displays.EmptyState
                  theme_empty_state
                  icon={<Icons.Building4 />}
                  title={`${isSearched ? "ไม่พบข้อมูลภารกิจที่คุณต้องการ" : "ไม่มีภารกิจองค์กร"}`}
                  subTitle={`${isSearched ? "กรุณาตรวจสอบเงื่อนไขการค้นหาอีกครั้ง" : 'กดปุ่ม "สร้างภารกิจ" เพื่อเริ่มต้นภารกิจแรก'}`}
                />
              }
              isUseResetSelect={false}
              onClickRow={(row) => handleViewDetail(row?.id)}
            />
          )}
        </div>
      </div>

      <div className="pagination_section">
        <PaginationNormal
          currentPage={pagination?.current_page || 1}
          fromOrder={pagination?.from || 0}
          toOrder={pagination?.to || 0}
          perPage={perPage || 20}
          totalPage={pagination?.total || 0}
          lastPage={pagination?.last_page || 1}
          onChangePerPage={handleChangePerPage}
          onClickPage={handleClickPage}
          widthbox="72px"
          perPageOptions={perPageOptions}
          alwaysShowArrows
        />
      </div>
    </CompanyMissionV2ContainerStyled>
  );
};

export default CompanyMissionV2Container;
